import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import {
  Table, Button, DatePicker, Row, Col, Checkbox, Modal, Tooltip, Tag, Popover, Badge, Icon, Typography
} from 'antd';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import { ActionCreate, ACTION_STATUS } from './ActionCreate';
import { FilterSearch } from '../ConstituentListPage/FilterSearch';
import * as actions from '../../../actions';
import { AccreditationsListClaimCard } from '../ConstituentPage/Accreditations';
import { DateRangeFilter } from '../../DateRangeFilter';

const PageWrapper = styled.div`
  border-radius: 12px;
  width: 90%;
  align-items: center;
  padding: 30px;
  margin: 30px auto 0;
  background-color: white;
  justify-content: center;
`;

const tagColorMapping = {
  green: 'green',
  red: 'red',
  yellow: 'gold',
};

const { Text } = Typography;

class ActionsPageComponent extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
      toDate: null,
      fromDate: null,
      completedFromDate: null,
      completedToDate: null,
      includeCompletedItems: false,
      showCompletedItemColumns: false,
      pagination: {},
      accreditationModalVisible: false,
      accreditationClaim: null,
    };
  }

  setQuery = searchQuery => {
    this.setState({ searchQuery });
  };

  componentDidMount() {
    this.props.getAllActionsAndDecisions();
    this.props.fetchActionCountByStatus(ACTION_STATUS.NEW);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.actionsAndDecisions !== this.props.actionsAndDecisions) {
      this.setState({
        pagination: {
          ...this.state.pagination,
          total: this.props.actionsAndDecisionsTotal,
          pageSize: 10,
        },
      });
    }
  }

  fetchActionsAndDecisionNext = (pagination, filters, sorter) => {
    const pager = { ...this.state.pagination };
    pager.current = pagination.current;
    this.setState({ pagination: pager });
    const opts = {
      constituent_name: this.state.searchQuery || null,
      ...(this.state.fromDate && { from_date: this.state.fromDate }),
      ...(this.state.toDate && { to_date: this.state.toDate }),
      completed_items: this.state.includeCompletedItems,
      offset: (pagination.current - 1) * 10,
    };
    this.props.getAllActionsAndDecisions(opts);
  };

  disableFuturedDate = current => current && current > moment().endOf('day');

  onDateChange = (dates, datesStr) => {
    if (!datesStr && !datesStr.length === 2) return;
    const [fromDate, toDate] = datesStr;
    const fromDateUtc = fromDate
      ? moment(fromDate).startOf('day').toDate().toISOString()
      : null;
    const toDateUtc = toDate
      ? moment(toDate).endOf('day').toDate().toISOString()
      : null;
    this.setState({ fromDate: fromDateUtc, toDate: toDateUtc });
  };

  handleRefresh = query => {
    const {
      includeCompletedItems,
      fromDate,
      toDate,
      completedFromDate,
      completedToDate,
      searchQuery,
    } = this.state;

    const activeFromDate = includeCompletedItems ? completedFromDate : fromDate;
    const activeToDate = includeCompletedItems ? completedToDate : toDate;

    this.setState({
      showCompletedItemColumns: !!includeCompletedItems,
      ...(includeCompletedItems && { fromDate: null, toDate: null }),
    });

    const opts = {
      ...(activeFromDate && { from_date: activeFromDate }),
      ...(activeToDate && { to_date: activeToDate }),
      constituent_name: query || searchQuery || null,
      completed_items: includeCompletedItems,
    };

    const shouldFetch = !includeCompletedItems || (activeFromDate && activeToDate);

    if (shouldFetch) {
      this.props.getAllActionsAndDecisions(opts);
      this.props.fetchActionCountByStatus(ACTION_STATUS.NEW);
    }
  };

  handleButtonRefresh = () => {
    this.handleRefresh();
  };

  onCompletedItemsChange = e => {
    this.setState({ includeCompletedItems: e.target.checked });
  };

  handleViewAccreditation = ({ accreditationId, constituentId }) => {
    this.props.fetchAccreditationClaim(constituentId, accreditationId).then(() => {
      const { accreditationClaim } = this.props;
      if (accreditationClaim) {
        this.setState({
          accreditationModalVisible: true,
          accreditationClaim,
        });
      }
    });
  };

  handleCloseAccreditationModal = () => {
    this.setState({
      accreditationModalVisible: false,
      accreditationClaim: null,
    });
  };

  getAttachmentInfo = accreditationAttachments => {
    accreditationAttachments = accreditationAttachments || [];

    if (!Array.isArray(accreditationAttachments) || accreditationAttachments.length === 0) {
      return <p>No attachments available</p>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '0.5rem' }}>
        {accreditationAttachments.map((attachment, index) => {
          const filename = attachment && attachment.filename ? attachment.filename : '';
          const displayName = filename.length > 0 && filename.length <= 20 ? filename : `Attachment ${index + 1}`;
          return (
            <Row key={(attachment && attachment.id) || index}>
              <Col span={20}>
                <a
                  href={attachment && attachment.download}
                  title={filename.length > 20 ? filename : ''}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {displayName}
                </a>
              </Col>
            </Row>
          );
        })}
      </div>
    );
  };

  renderChecks = (record = {}) => {
    if (!record || typeof record !== 'object') return null;

    const accType = record.accreditationType;
    const attachments = record.accreditationAttachments || [];
    const name = accType && String(accType).toLowerCase() || '';
    const statusColor = record.claimStatusColor;
    const isCompleted = !!record.actionTaken;

    // Convert red to orange for pending decisions
    const displayColor = (!isCompleted && statusColor === 'red') ? 'orange' : statusColor;
    const isPendingDecision = !isCompleted && statusColor === 'red';

    // Determine tag props based on completion
    const tagProps = !isCompleted
      ? {
        style: {
          fontSize: '16px',
          border: '1px dashed',
          borderRadius: '2px',
          display: 'flex',
          width: 'fit-content',
          padding: '0.2rem',
          margin: '0.5rem',
        },
      }
      : {
        color: tagColorMapping[statusColor] || null,
        style: {
          fontSize: '16px',
          display: 'flex',
          width: 'fit-content',
          padding: '0.2rem',
          margin: '0.5rem',
        },
      };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Tag {...tagProps}>
          <div style={{ display: "flex", alignItems: "center", gap: "4px", marginLeft: "4px" }}>
            {!isCompleted && (
              <>
                <Badge color={displayColor} />
                {isPendingDecision && (
                  <Icon type="exclamation-circle" style={{ color: '#faad14', marginLeft: '4px' }} />
                )}
              </>
            )}
            <span>{name}</span>
            {isPendingDecision && (
              <span style={{ marginLeft: '8px', color: '#faad14', fontStyle: 'italic' }}>
                Awaiting Decision
              </span>
            )}
          </div>
          <Popover
            placement="right"
            trigger="click"
            content={this.getAttachmentInfo(attachments)}
            title={
              <p>
                Attachments for{" "}
                <strong style={{ color: "#82412C" }}>{name}</strong>
              </p>
            }
          >
            <div style={{ display: "flex", marginLeft: "0.5rem" }}>
              <Badge count={attachments.length} style={{ backgroundColor: "#4B9CD3" }}>
                <Icon
                  style={{
                    display: "flex",
                    color: "#82412C",
                    fontSize: "20px",
                    cursor: "pointer",
                  }}
                  type="paper-clip"
                />
              </Badge>
            </div>
          </Popover>
        </Tag>
        <Text style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => this.handleViewAccreditation({ accreditationId: record.accreditationId, constituentId: record.constituentId })}>
          View
        </Text>
      </div>
    );
  };

  render() {
    const { actionsAndDecisions } = this.props;
    const items = actionsAndDecisions ? actionsAndDecisions.toJS() : [];
    const { pagination, accreditationModalVisible, accreditationClaim } = this.state;
    const constituent = accreditationClaim && accreditationClaim.get('constituent');
    const accreditation = accreditationClaim && accreditationClaim.get('accreditation');
    const attachments = accreditationClaim && accreditationClaim.get('attachments');
    const {
      attachments: _attachments,
      constituent: _constituent,
      accreditation: _accreditation,
      ...data
    } = (accreditationClaim && accreditationClaim.toJS()) || {};
    const baseColumns = [
      {
        title: 'Reference',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Constituent',
        dataIndex: ['constituent', 'fullName'],
        key: 'constituent_full_name',
        width: 100,
        render: (text, record) => (
          <Link to={`/constituents/${record.constituent.id}`}>{text}</Link>
        ),
      },
      {
        title: 'Submitted',
        dataIndex: 'createdAt',
        key: 'created_at',
        render: value => moment(value).format('DD/MM/YYYY'),
      },
      {
        title: 'Check Type',
        key: 'accreditation_type',
        render: (_, record) => this.renderChecks(record),
      },
      {
        title: 'Decision Type',
        dataIndex: ['actionType', 'type'],
        key: 'action_type',
        className: 'nowrap-column',
        render: value => (value
          ? value
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
          : 'N/A'),
      },
    ];

    const pendingColumns = [
      ...baseColumns,
      {
        title: 'Action',
        key: 'action',
        width: 300,
        render: record => (
          <ActionCreate
            record={record}
          />
        ),
      },
    ];

    const toTitleCase = str => (str
      ? str
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      : '');
    const completedColumns = [
      ...baseColumns,
      {
        title: 'Action Taken',
        dataIndex: ['actionTaken'],
        key: 'action_taken',
        className: 'nowrap-column',
        render: value => toTitleCase(value) || 'N/A',
      },
      {
        title: 'Action Taken At',
        dataIndex: ['takenTimestamp'],
        key: 'taken_timestamp',
        className: 'nowrap-column',
        render: value => moment(value).format('DD/MM/YYYY'),
      },
      {
        title: 'Action Taken By',
        dataIndex: ['takenBy'],
        key: 'taken_by',
        className: 'nowrap-column',
        render: value => (value || 'N/A'),
      },
    ];

    return (
      <PageWrapper>
        <Row type="flex" justify="space-between" align="middle">
          <Col>
            <FilterSearch
              query={this.state.searchQuery}
              allowSearchOnKeyChange
              setQuery={this.setQuery}
              onEnter={this.handleRefresh}
            />
          </Col>
          <Col
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div style={{ marginRight: '26px', width: '240px' }}>
              {this.state.showCompletedItemColumns ? (
                <DateRangeFilter
                  onChange={(fromDate, toDate) => {
                    this.setState({ completedFromDate: fromDate, completedToDate: toDate }, this.handleRefresh);
                  }}
                />
              ) : (
                <DatePicker.RangePicker
                  ranges={{
                    Today: [moment().startOf('day'), moment().endOf('day')],
                    'Last 7 Days': [
                      moment().subtract(6, 'days').startOf('day'),
                      moment().endOf('day'),
                    ],
                    'This Month': [
                      moment().startOf('month'),
                      moment().endOf('day'),
                    ],
                  }}
                  onChange={this.onDateChange}
                  disabledDate={this.disableFuturedDate}
                />
              )}
            </div>
            <div style={{ marginRight: '16px' }}>
              <Checkbox
                onChange={this.onCompletedItemsChange}
                checked={this.state.includeCompletedItems}
              >
                Completed Items
              </Checkbox>
            </div>
            <Button type="primary" onClick={this.handleButtonRefresh}>
              Refresh
            </Button>
          </Col>
        </Row>
        <Row style={{ marginTop: '30px' }}>
          <Table
            bordered={true}
            dataSource={items}
            columns={
              this.state.showCompletedItemColumns
                ? completedColumns
                : pendingColumns
            }
            pagination={pagination}
            onChange={this.fetchActionsAndDecisionNext}
            style={{ tableLayout: 'auto' }}
            scroll={{ x: true }}
          />
        </Row>
        {/* Accreditation modal */}
        {accreditationModalVisible && accreditationClaim && (
          <Modal
            visible={accreditationModalVisible}
            onCancel={this.handleCloseAccreditationModal}
            footer={null}
            destroyOnClose
          >
            <div style={{ marginTop: '24px' }}>
              <AccreditationsListClaimCard
                constituent={constituent}
                accreditation={accreditation}
                attachments={attachments}
                patching={false}
                role="accreditation"
                viewOnly={true}
                {...data}
              />
            </div>
          </Modal>
        )}
      </PageWrapper>
    );
  }
}

export const ActionsPage = connect(
  state => ({
    actionsAndDecisions: state.doc.getIn(['http', 'actionsAndDecisions', 'data', 'items']),
    accreditationClaim: state.doc.getIn(['http', 'fetchAccreditationClaim', 'data']),
    scanTypes: state.doc.getIn(['http', 'scanTypes', 'data'], {}),
    actionsAndDecisionsTotal: state.doc.getIn(['http', 'actionsAndDecisions', 'data', 'total']),
    actionPendingCount: state.doc.getIn(['http', 'fetchActionCountByStatus', 'data'], null),
  }),
  actions,
)(ActionsPageComponent);
